import { useState } from 'react'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft, faCrown, faEyeSlash, faComments, faIdCard,
  faPaperclip, faClockRotateLeft, faUser, faBuilding, faGlobe,
  faShieldHalved, faFileLines
} from '@fortawesome/free-solid-svg-icons'
import SuperadminSidebar from '../../../components/superadmin/Sidebar'
import SuperadminTopbar from '../../../components/superadmin/Topbar'

const STATUS_STYLE = {
  menunggu: 'bg-blue-900/50 text-blue-300 ring-blue-700',
  diproses: 'bg-amber-900/40 text-amber-300 ring-amber-700',
  selesai:  'bg-emerald-900/40 text-emerald-300 ring-emerald-700',
  ditolak:  'bg-red-900/40 text-red-300 ring-red-700',
}

export default function SuperadminReportsShow({ current_user, report }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  if (!report) return null

  return (
    <div className="flex h-screen bg-gray-950 font-sans overflow-hidden text-gray-100">
      <SuperadminSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <SuperadminTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <a href="/superadmin/reports" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-400 mb-5 transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} className="w-3.5 h-3.5" />
            Kembali ke Semua Laporan
          </a>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faCrown} className="w-4 h-4 text-indigo-400" />
                <span className="text-xs font-mono text-gray-500">{report.tracking_code}</span>
                {report.anonymous && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                    <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3" /> Anonim
                  </span>
                )}
              </div>
              <h1 className="text-xl font-bold text-white">{report.title}</h1>
              <p className="text-sm text-gray-500 mt-1">{report.category} · Dibuat {report.created_at}</p>
            </div>
            <span className={`inline-flex self-start px-3 py-1.5 rounded-full text-sm font-semibold ring-1 ${STATUS_STYLE[report.status]}`}>
              {report.status_label}
            </span>
          </div>

          {current_user?.admin && (
            <div className="mb-6 bg-indigo-900/20 border border-indigo-800/50 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-indigo-200">
                <FontAwesomeIcon icon={faShieldHalved} className="w-3.5 h-3.5 mr-2" />
                Halaman ini untuk oversight. Untuk menindaklanjuti laporan, gunakan Panel Admin.
              </p>
              <a
                href={`/admin/reports/${report.id}`}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg whitespace-nowrap"
              >
                Kelola di Panel Admin →
              </a>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2 bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-200 mb-3">Uraian Laporan</h2>
              <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{report.description}</p>

              {(report.location || report.incident_date) && (
                <div className="mt-4 pt-4 border-t border-gray-800 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {report.location && (
                    <div>
                      <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">Lokasi</p>
                      <p className="text-gray-300">{report.location}</p>
                    </div>
                  )}
                  {report.incident_date && (
                    <div>
                      <p className="text-xs text-gray-600 uppercase tracking-wider mb-1">Tanggal Kejadian</p>
                      <p className="text-gray-300">{report.incident_date}</p>
                    </div>
                  )}
                </div>
              )}

              {report.attachments?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Lampiran ({report.attachments.length})</p>
                  <ul className="space-y-2">
                    {report.attachments.map((a, i) => (
                      <li key={i}>
                        <a href={a.url} className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300">
                          <FontAwesomeIcon icon={faPaperclip} className="w-3.5 h-3.5" />
                          {a.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faUser} className="w-3.5 h-3.5 text-indigo-400" />
                  Pelapor
                </h2>
                {report.identity_reporter ? (
                  <dl className="space-y-2 text-sm">
                    <div><dt className="text-gray-600 text-xs">Nama</dt><dd className="text-gray-300">{report.identity_reporter.name || '—'}</dd></div>
                    <div><dt className="text-gray-600 text-xs">Email</dt><dd className="text-gray-300">{report.identity_reporter.email}</dd></div>
                    <div><dt className="text-gray-600 text-xs">Telepon</dt><dd className="text-gray-300">{report.identity_reporter.phone || '—'}</dd></div>
                    <div><dt className="text-gray-600 text-xs">NIK</dt><dd className="text-gray-300 font-mono">{report.identity_reporter.nik || '—'}</dd></div>
                    <p className="text-xs text-emerald-400 mt-2">Identitas telah dibuka &amp; disetujui</p>
                  </dl>
                ) : report.anonymous ? (
                  <p className="text-sm text-gray-500">Laporan anonim — identitas belum dibuka</p>
                ) : (
                  <dl className="space-y-2 text-sm">
                    <div><dt className="text-gray-600 text-xs">Nama</dt><dd className="text-gray-300">{report.reporter?.label}</dd></div>
                    {report.reporter?.email && (
                      <div><dt className="text-gray-600 text-xs">Email</dt><dd className="text-gray-300">{report.reporter.email}</dd></div>
                    )}
                    {report.reporter?.type && (
                      <div className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                        <FontAwesomeIcon icon={report.reporter.type === 'internal' ? faBuilding : faGlobe} className="w-3 h-3" />
                        {report.reporter.type}
                      </div>
                    )}
                  </dl>
                )}
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h2 className="text-sm font-semibold text-gray-200 mb-3">Indikator</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Komunikasi</span>
                    <span className={report.communication_open ? 'text-sky-400' : 'text-gray-600'}>
                      {report.communication_open ? 'Terbuka' : 'Tertutup'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Permintaan identitas</span>
                    <span className={report.identity_reveal_requested ? 'text-amber-400' : 'text-gray-600'}>
                      {report.identity_reveal_requested ? (report.identity_reveal_approved ? 'Disetujui' : 'Menunggu') : 'Tidak ada'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">PKS ditandatangani</span>
                    <span className={report.has_pks_signature ? 'text-emerald-400' : 'text-gray-600'}>
                      {report.has_pks_signature ? 'Ya' : 'Belum'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Komentar</span>
                    <span className="text-gray-300">{report.comments_count ?? report.comments?.length ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faClockRotateLeft} className="w-3.5 h-3.5 text-indigo-400" />
                Riwayat Aktivitas
              </h2>
              {report.activities?.length === 0 ? (
                <p className="text-sm text-gray-500">Belum ada aktivitas.</p>
              ) : (
                <ul className="space-y-3">
                  {report.activities.map(a => (
                    <li key={a.id} className="border-l-2 border-indigo-800 pl-3">
                      <p className="text-sm text-gray-300">{a.action_label}</p>
                      {a.admin_name && <p className="text-xs text-gray-600">oleh {a.admin_name}</p>}
                      {a.note && <p className="text-xs text-gray-500 mt-0.5">{a.note}</p>}
                      <p className="text-xs text-gray-600 mt-0.5">{a.created_at}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faComments} className="w-3.5 h-3.5 text-indigo-400" />
                Komentar Terbaru
              </h2>
              {report.comments?.length === 0 ? (
                <p className="text-sm text-gray-500">Belum ada komentar.</p>
              ) : (
                <ul className="space-y-3">
                  {report.comments.map(c => (
                    <li key={c.id} className="bg-gray-800/50 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-semibold text-gray-300">{c.user_name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.role === 'admin' ? 'bg-blue-900/50 text-blue-300' : 'bg-gray-700 text-gray-400'}`}>
                          {c.role}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">{c.body}</p>
                      <p className="text-xs text-gray-600 mt-1">{c.created_at}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
